import { useState, useEffect } from 'react';
import OptionsRangeFilter, { FilterOption } from '@/components/filters/OptionsRangeFilter/OptionsRangeFilter';
import { formatISODate } from '@/helpers/dateHelper';

interface DateFilterProps {
  dateFrom: Date;
  dateTo: Date;
  onChange: (dateFrom: Date, dateTo: Date) => void;
  /** HAT-1212: refetch בעמוד → disabled ויזואלי לחצי החודש. */
  isBusy?: boolean;
}

type PresetId = 'last90Days' | 'lastYear';

const getPresetRange = (presetId: PresetId): { from: string; to: string } => {
  const now = new Date();

  switch (presetId) {
    case 'last90Days': {
      const dateFrom = new Date(now);
      dateFrom.setDate(dateFrom.getDate() - 90);
      return { from: formatISODate(dateFrom), to: formatISODate(now) };
    }
    case 'lastYear': {
      const dateFrom = new Date(now);
      dateFrom.setFullYear(dateFrom.getFullYear() - 1);
      return { from: formatISODate(dateFrom), to: formatISODate(now) };
    }
  }
};

const DateFilter = ({ dateFrom, dateTo, onChange, isBusy }: DateFilterProps) => {
  const [fromValue, setFromValue] = useState<string>(formatISODate(dateFrom));
  const [toValue, setToValue] = useState<string>(formatISODate(dateTo));
  const [appliedRange, setAppliedRange] = useState<{ from: string; to: string } | null>({
    from: formatISODate(dateFrom),
    to: formatISODate(dateTo),
  });

  const [options, setOptions] = useState<FilterOption[]>([
    { id: 'last90Days', label: 'last90Days', checked: false },
    { id: 'lastYear', label: 'dateFilter.lastYear', checked: false },
  ]);

  useEffect(() => {
    const newFrom = formatISODate(dateFrom);
    const newTo = formatISODate(dateTo);
    setFromValue(newFrom);
    setToValue(newTo);
    setAppliedRange({ from: newFrom, to: newTo });
  }, [dateFrom, dateTo]);

  const handleOptionChange = (id: string) => {
    const presetId = id as PresetId;
    const range = getPresetRange(presetId);

    setOptions((prev) =>
      prev.map((opt) => ({
        ...opt,
        checked: opt.id === id,
      }))
    );

    setAppliedRange(null);
    onChange(new Date(range.from), new Date(range.to));
  };

  const handleRangeApply = (from: string, to: string) => {
    setOptions((prev) => prev.map((opt) => ({ ...opt, checked: false })));
    onChange(new Date(from), new Date(to));
  };

  return (
    <OptionsRangeFilter
      title="dateRange"
      filterType="options-and-range"
      selectionType="radio"
      options={options}
      onOptionChange={handleOptionChange}
      rangeConfig={{
        type: 'date',
        fromPlaceholder: 'fromDate',
        toPlaceholder: 'toDate',
      }}
      fromValue={fromValue}
      setFromValue={setFromValue}
      toValue={toValue}
      setToValue={setToValue}
      appliedRange={appliedRange}
      setAppliedRange={setAppliedRange}
      onRangeApply={handleRangeApply}
      showMonthNavigation
      hebrewCalendar
      isBusy={isBusy}
    />
  );
};

export default DateFilter;
